// ============================================================
// AccidentDetailsSection.tsx
// Stage 2 redesign (approved mockup casevo-case-sections-batch1.html §2):
// the shared SectionCards view/edit pattern — When · Location · What
// happened · Claimant vehicle — with an at-a-glance strip and the shared
// section footer. DATA LOGIC IS UNCHANGED from the old long form:
//   • every input writes through `update` (live-store spread → updateCase →
//     debounced case autosave), same fieldKeys (presence + live sync)
//   • location mode buttons: update + broadcastFieldEditNow (live reveal)
//   • free-text location = the same Yjs RichTextEditor (doc
//     case:<id>:accidentLocationFreeText, same key/props/onSnapshot)
//   • AddressLookup writes all 5 fields in ONE updateCase + broadcasts
//   • postcode / claimant reg uppercased, time null when empty, people 0-99
//   • validateAccidentDetails gates Mark complete (SectionFooter)
//   • ACCIDENT DATE (date hardening): a half-typed date is never saved
//     (DateInput); a future date or one before the client's DOB is refused
//     on the spot (utils/caseDateRules — the server 400s the same) and isn't
//     autosaved; the server's refusal shows under the field; amber if the
//     accident is more than 3 years ago.
// ============================================================
import React from 'react'
import { useCasevoStore } from '../../store/useCasevoStore'
import { forOpenCase } from '../../utils/caseSnapshotGuard'
import { AccidentDetails } from '../../types/case.types'
import FormField from '../shared/FormField'
import FieldError from '../shared/FieldError'
import DateInput from '../shared/DateInput'
import { accidentDateError, accidentDateWarning, serverDateErrorFor } from '../../utils/caseDateRules'
import { londonToday } from '../../utils/civilDate'
import { caseDobOf } from '../../utils/caseDob'
import { caseScope, useFieldRefusal } from '../../hooks/fieldRefusals'
import DateTimeRow from '../shared/DateTimeRow'
import AddressLookup from '../shared/AddressLookup'
import RichTextEditor from '../shared/RichTextEditor'
import { useCollabUser } from '../../hooks/useCollabUser'
import DOMPurify from 'dompurify'
import { accidentDateOptional, accidentFieldRequired, DISEASE_ACCIDENT_HINT, DISEASE_LOCATION_HINT, validateAccidentDetails } from '../../utils/validation'
import { useShouldShowOnboardingPrompt } from '../../hooks/useOnboardingPrompt'
import { broadcastFieldEdits, broadcastFieldEditNow } from '../../hooks/usePresence'
import { flushAutoSave } from '../../hooks/useAutoSave'
import { INP, inpCls, SELECT_BG, FGRID } from '../clientInfo/clientInfoRegistry'
import SectionCards, { type CardDef } from '../case/sections/SectionCards'
import {
  KIcon, Glance, GlanceDash, SectionFooter, Kv, Empty, Muted, YesNo, AddressBlock, CondLabel, SegRadios, SegButtons,
  LiveDocEdit, Clamp, LiveDocView, DL, DL1, BIG, MONO, CHIP, CHIP_N, PLATE, REVEALED, HINT, htmlHasText, htmlText,
} from '../case/sections/sectionKit'

// Live edit-sync: address-lookup writes all 5 location fields programmatically
// (no per-field blur), so onSelect/onClear broadcast these fieldKeys explicitly.
const ACCIDENT_ADDRESS_FIELD_KEYS = ['accident.address-line-1', 'accident.address-line-2', 'accident.town', 'accident.county', 'accident.postcode']
// Field → its existing fieldKey (presence / live sync). Used for the Cancel
// restore broadcast; the inputs carry the same keys on their FormFields.
const FIELD_KEY: Partial<Record<keyof AccidentDetails, string>> = {
  askCueReference: 'accident.askcue-reference',
  accidentDate: 'accident.accident-date',
  accidentTime: 'accident.accident-time',
  vehiclePosition: 'accident.driver-or-passenger',
  driverName: 'accident.driver-name',
  accidentDescription: 'accident.accident-description',
  accidentDescriptionOther: 'accident.please-describe',
  accidentLocationMode: 'accident.accident-location',
  accidentLocationAddressLine1: 'accident.address-line-1',
  accidentLocationAddressLine2: 'accident.address-line-2',
  accidentLocationAddressTown: 'accident.town',
  accidentLocationAddressCounty: 'accident.county',
  accidentLocationAddressPostcode: 'accident.postcode',
  wearingSeatbelt: 'accident.wearing-a-seatbelt',
  claimantReg: 'accident.claimant-reg',
  claimantVehicleResponsible: 'accident.was-the-claimant-a-passenger-in-the-vehicle-responsible',
  numberOfPeopleInVehicle: 'accident.number-of-people-in-the-claimant-vehicle',
  policeInvolvement: 'accident.police-involvement',
}
const DESCRIPTIONS: { value: string; label: string }[] = [
  { value: 'hit in the rear', label: 'Hit in the rear' },
  { value: 'out of side road', label: 'Out of side road' },
  { value: 'changed lanes', label: 'Changed lanes' },
  { value: 'concertina', label: 'Concertina' },
  { value: 'hit whilst parked', label: 'Hit whilst parked' },
  { value: 'hit by oncoming car', label: 'Hit by oncoming car' },
  { value: 'other', label: 'Other' },
]
const descLabel = (v: string | null): string | null => (v ? DESCRIPTIONS.find((d) => d.value === v)?.label ?? v : null)

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const civil = (iso: string): Date | null => {
  const d = new Date(`${iso.slice(0, 10)}T00:00:00`)
  return Number.isNaN(d.getTime()) ? null : d
}
const longDate = (iso: string): string => {
  const d = civil(iso)
  // Mockup "Sat 12 September 2026" (no comma — en-GB inserts one).
  return d ? `${d.toLocaleDateString('en-GB', { weekday: 'short' })} ${d.getDate()} ${d.toLocaleDateString('en-GB', { month: 'long' })} ${d.getFullYear()}` : iso
}
const shortDate = (iso: string): string => {
  const d = civil(iso)
  return d ? `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}` : iso
}
const daysAgo = (iso: string): string => {
  const d = civil(iso)
  if (!d) return ''
  const t = new Date(); t.setHours(0, 0, 0, 0)
  const n = Math.round((t.getTime() - d.getTime()) / 86400000)
  return n === 0 ? 'today' : n === 1 ? 'yesterday' : n > 0 ? `${n} days ago` : `in ${-n} days`
}

const AccidentDetailsSection: React.FC = () => {
  const { currentCase, updateCase } = useCasevoStore()
  const saveError = useCasevoStore((s) => s.saveError)
  // The server's refusal of the accident date (it went back to the saved
  // value; the rest of the batch saved) — shown under the field until re-edited.
  const refusedAccident = useFieldRefusal(currentCase ? caseScope(currentCase.id) : null, 'accidentDate')
  // A date the rules refuse (future / before DOB): shown, never autosaved.
  const [accidentBlock, setAccidentBlock] = React.useState<string | null>(null)
  // "Empty" = every relevant field is at its initial value. Computed
  // before the early return so the hook below runs unconditionally.
  const accident = currentCase?.accidentDetails
  const isEmpty = !!accident && (
    !accident.askCueReference && !accident.accidentDate && !accident.accidentTime
    && !accident.vehiclePosition && !accident.driverName
    && !accident.accidentDescription && !accident.accidentDescriptionOther
    && !accident.accidentLocation && accident.wearingSeatbelt === null
    && !accident.claimantReg && accident.claimantVehicleResponsible === null
    && accident.numberOfPeopleInVehicle === null && accident.policeInvolvement === null
  )
  const showPrompt = useShouldShowOnboardingPrompt(isEmpty)
  // Collab identity for the Yjs carets on the free-text editor. Hook must run
  // before the early return below.
  const collabUser = useCollabUser()
  if (!currentCase || !accident) return null
  const errors = validateAccidentDetails(currentCase)
  // Spread the LIVE store object, not the render-time `accident`: the
  // rich-text snapshot fires ~1.5s after typing (a stale closure), which
  // otherwise reverted any field changed in between (e.g. a seatbelt tick
  // or a co-editor's remote edit).
  const update = (field: keyof AccidentDetails, value: unknown) => {
    const live = useCasevoStore.getState().currentCase?.accidentDetails ?? accident
    updateCase({ accidentDetails: { ...live, [field]: value } })
  }
  // Multi-field write (Cancel restore) — one updateCase, live spread.
  const updateMany = (changes: Partial<AccidentDetails>) => {
    const live = useCasevoStore.getState().currentCase?.accidentDetails ?? accident
    updateCase({ accidentDetails: { ...live, ...changes } })
  }
  const a = accident
  const freeText = a.accidentLocationMode === 'freetext'
  const freeTextDoc = `case:${currentCase.id}:accidentLocationFreeText`
  const addrLines = [a.accidentLocationAddressLine1, a.accidentLocationAddressLine2, [a.accidentLocationAddressTown, a.accidentLocationAddressCounty].filter((s) => s.trim()).join(', ')]
    .map((s) => s.trim()).filter(Boolean)
  const pc = a.accidentLocationAddressPostcode.trim()
  const isPassenger = a.vehiclePosition === 'passenger'

  const defs: CardDef<AccidentDetails>[] = [
    {
      key: 'when', title: 'When', sub: 'Date, time and reference', icon: <KIcon name="cal" size={17} />,
      fields: ['accidentDate', 'accidentTime', 'askCueReference'], errorKeys: ['accidentDate'],
      view: (ctx) => (
        <dl className={DL}>
          <Kv label="Date of accident" full>{a.accidentDate ? <span className={`${BIG} tabular-nums`}>{longDate(a.accidentDate)}</span> : <Empty onAdd={ctx.onAdd} />}</Kv>
          <Kv label="Accident time">{a.accidentTime ? <span className="tabular-nums">{a.accidentTime}</span> : <Empty onAdd={ctx.onAdd} />}</Kv>
          <Kv label="AskCue reference">{a.askCueReference.trim() ? <span className={MONO}>{a.askCueReference}</span> : <Empty onAdd={ctx.onAdd} />}</Kv>
        </dl>
      ),
      edit: (ctx) => {
        const dateErr = ctx.err('accidentDate')
        const today = londonToday()
        const serverErr = refusedAccident ?? serverDateErrorFor(saveError, 'accidentDate')
        const warn = accidentDateWarning(a.accidentDate, today)
        return (
          <div className={FGRID}>
            {/* Date + time share one wrapping row (DateTimeRow), not two grid
                columns: a narrow column let the fixed-width date overflow
                over the time field. */}
            <DateTimeRow
              className="sm:col-span-2"
              date={
                <FormField variant="info" label="Date of accident" fieldKey="accident.accident-date" required={!accidentDateOptional(currentCase)}>
                  <DateInput
                    value={a.accidentDate}
                    onChange={(value) => {
                      const block = accidentDateError(value, caseDobOf(currentCase.clientDetails?.dateOfBirth, currentCase.client?.dateOfBirth).dob, londonToday())
                      setAccidentBlock(block)
                      if (!block) update('accidentDate', value)
                    }}
                    ariaLabel="Date of accident"
                    fieldClassName={`h-[38px] !rounded-[9px] !bg-casevo-input focus:!bg-casevo-surface ${dateErr || accidentBlock || serverErr ? 'border-casevo-danger' : 'border-casevo-border'}`}
                  />
                  <FieldError message={accidentBlock ?? serverErr ?? dateErr ?? undefined} />
                  {!accidentBlock && !serverErr && warn && <span className="block text-[12px] text-casevo-warn mt-1" role="status">{warn}</span>}
                  {/* (B2b fix 2) optional for EL disease */}
                  {accidentDateOptional(currentCase) && <span className="block text-[12px] text-casevo-text-soft mt-1">{DISEASE_ACCIDENT_HINT}</span>}
                </FormField>
              }
              time={
                <FormField variant="info" label="Accident time" fieldKey="accident.accident-time" htmlFor={`${ctx.idPrefix}-time`}>
                  <input id={`${ctx.idPrefix}-time`} type="time" value={a.accidentTime || ''} onChange={(e) => update('accidentTime', e.target.value || null)} className={inpCls(false)} />
                </FormField>
              }
            />
            <div className="sm:col-span-2">
              <FormField variant="info" label="AskCue reference" fieldKey="accident.askcue-reference" htmlFor={`${ctx.idPrefix}-askcue`}>
                <input id={`${ctx.idPrefix}-askcue`} type="text" value={a.askCueReference} onChange={(e) => update('askCueReference', e.target.value)} className={`${inpCls(false)} font-mono tracking-[0.04em]`} />
              </FormField>
            </div>
          </div>
        )
      },
    },
    {
      key: 'location', title: 'Location', sub: 'Where it happened', icon: <KIcon name="map" size={17} />, full: 'edit',
      fields: ['accidentLocationMode', 'accidentLocationAddressLine1', 'accidentLocationAddressLine2', 'accidentLocationAddressTown', 'accidentLocationAddressCounty', 'accidentLocationAddressPostcode'],
      errorKeys: ['accidentLocation'],
      hint: freeText ? 'Mode saves instantly · text is live' : undefined,
      view: (ctx) => (
        <dl className={DL1}>
          <Kv label="Accident location">
            {freeText ? (
              // LIVE read-only view (saved copy until synced / if collab is down).
              <div className="min-w-0 w-full">
                <Clamp moreLabel="Open to edit" onMore={ctx.onAdd} showMore={htmlHasText(a.accidentLocationFreeText)}>
                  <LiveDocView
                    docName={freeTextDoc}
                    html={a.accidentLocationFreeText}
                    onSnapshot={forOpenCase(currentCase.id, (html) => update('accidentLocationFreeText', html))}
                    collabUser={collabUser}
                    empty={<Empty onAdd={ctx.onAdd} />}
                  />
                </Clamp>
              </div>
            ) : addrLines.length || pc ? (
              <AddressBlock lines={addrLines} postcode={pc || undefined} />
            ) : a.accidentLocation.trim() ? (
              <span className="min-w-0">{a.accidentLocation}</span>
            ) : <Empty onAdd={ctx.onAdd} />}
          </Kv>
          <Kv label="Recorded as">
            {freeText
              ? <span className={CHIP_N}><KIcon name="text" size={12} />Free text</span>
              : <><span className={CHIP_N}><KIcon name="map" size={12} />Address</span><span className="text-[12px] text-casevo-text-soft">Switch to free text for roads or junctions without a postcode</span></>}
          </Kv>
        </dl>
      ),
      edit: (ctx) => {
        const locErr = ctx.err('accidentLocation')
        return (
          <div className={FGRID}>
            <div className="sm:col-span-2">
              <FormField variant="info" label="Accident location" fieldKey="accident.accident-location" required={accidentFieldRequired('accidentLocation', currentCase)}>
                <SegButtons
                  ariaLabel="Accident location type"
                  options={[
                    {
                      label: <><KIcon name="map" size={14} />Address</>,
                      checked: (a.accidentLocationMode ?? 'address') === 'address',
                      onSelect: () => {
                        // Sync the MODE immediately so the free-text editor appears/
                        // disappears live for other viewers.
                        update('accidentLocationMode', 'address')
                        broadcastFieldEditNow('accident.accident-location')
                      },
                    },
                    {
                      label: <><KIcon name="text" size={14} />Free text</>,
                      checked: freeText,
                      onSelect: () => {
                        update('accidentLocationMode', 'freetext')
                        broadcastFieldEditNow('accident.accident-location')
                      },
                    },
                  ]}
                />
                {/* (B2b fix 3) optional for EL disease */}
                {!accidentFieldRequired('accidentLocation', currentCase) && <span className="block text-[12px] text-casevo-text-soft mt-1">{DISEASE_LOCATION_HINT}</span>}
                {freeText && (
                  <div className="mt-3">
                    {/* Accident free-text — Yjs collaborative rich-text. Doc
                        `case:<id>:accidentLocationFreeText`; conditional on the
                        SHARED mode flag (synced live above). Same props as before. */}
                    <LiveDocEdit>
                      {(onStatus) => (
                        <RichTextEditor
                          key={freeTextDoc}
                          collabDocName={freeTextDoc}
                          collabToken={() => localStorage.getItem('casevo_token') ?? ''}
                          initialHtml={DOMPurify.sanitize(a.accidentLocationFreeText ?? '')}
                          onSnapshot={forOpenCase(currentCase.id, (html) => update('accidentLocationFreeText', html))}
                          placeholder="e.g. M62 westbound, junction 18-19"
                          collabUser={collabUser}
                          onCollabStatus={onStatus}
                        />
                      )}
                    </LiveDocEdit>
                  </div>
                )}
                <FieldError message={locErr} />
              </FormField>
            </div>
            {!freeText && (
              <>
                <div className="sm:col-span-2 min-w-0">
                  <label htmlFor={`${ctx.idPrefix}-search`} className="block text-[12px] font-semibold text-casevo-brand dark:text-casevo-mid mb-[5px]">Search address</label>
                  {/* Picks a suggestion and writes all 5 fields in ONE updateCase
                      call (five update() calls would stale-spread). */}
                  <AddressLookup
                    variant="info"
                    inputId={`${ctx.idPrefix}-search`}
                    value={
                      a.accidentLocationAddressLine1 || a.accidentLocationAddressLine2 ||
                      a.accidentLocationAddressTown || a.accidentLocationAddressCounty ||
                      a.accidentLocationAddressPostcode
                        ? {
                            line1: a.accidentLocationAddressLine1,
                            line2: a.accidentLocationAddressLine2,
                            town: a.accidentLocationAddressTown,
                            county: a.accidentLocationAddressCounty,
                            postcode: a.accidentLocationAddressPostcode,
                          }
                        : null
                    }
                    onSelect={(addr) => {
                      // Live object (updateMany), not the render-time spread —
                      // the lookup resolves async, so `accident` may be stale.
                      updateMany({
                        accidentLocationAddressLine1: addr.line1,
                        accidentLocationAddressLine2: addr.line2,
                        accidentLocationAddressTown: addr.town,
                        accidentLocationAddressCounty: addr.county,
                        accidentLocationAddressPostcode: addr.postcode,
                      })
                      broadcastFieldEdits(ACCIDENT_ADDRESS_FIELD_KEYS)
                    }}
                    onClear={() => {
                      updateMany({
                        accidentLocationAddressLine1: '',
                        accidentLocationAddressLine2: '',
                        accidentLocationAddressTown: '',
                        accidentLocationAddressCounty: '',
                        accidentLocationAddressPostcode: '',
                      })
                      broadcastFieldEdits(ACCIDENT_ADDRESS_FIELD_KEYS)
                    }}
                  />
                </div>
                <FormField variant="info" label="Address line 1" fieldKey="accident.address-line-1" htmlFor={`${ctx.idPrefix}-l1`}>
                  <input id={`${ctx.idPrefix}-l1`} type="text" value={a.accidentLocationAddressLine1} onChange={(e) => update('accidentLocationAddressLine1', e.target.value)} className={inpCls(false)} />
                </FormField>
                <FormField variant="info" label="Address line 2" fieldKey="accident.address-line-2" htmlFor={`${ctx.idPrefix}-l2`}>
                  <input id={`${ctx.idPrefix}-l2`} type="text" value={a.accidentLocationAddressLine2} onChange={(e) => update('accidentLocationAddressLine2', e.target.value)} className={inpCls(false)} />
                </FormField>
                <FormField variant="info" label="Town" fieldKey="accident.town" htmlFor={`${ctx.idPrefix}-town`}>
                  <input id={`${ctx.idPrefix}-town`} type="text" value={a.accidentLocationAddressTown} onChange={(e) => update('accidentLocationAddressTown', e.target.value)} className={inpCls(false)} />
                </FormField>
                <FormField variant="info" label="County" fieldKey="accident.county" htmlFor={`${ctx.idPrefix}-county`}>
                  <input id={`${ctx.idPrefix}-county`} type="text" value={a.accidentLocationAddressCounty} onChange={(e) => update('accidentLocationAddressCounty', e.target.value)} className={inpCls(false)} />
                </FormField>
                <FormField variant="info" label="Postcode" fieldKey="accident.postcode" htmlFor={`${ctx.idPrefix}-pc`}>
                  <input
                    id={`${ctx.idPrefix}-pc`}
                    type="text"
                    value={a.accidentLocationAddressPostcode}
                    onChange={(e) => update('accidentLocationAddressPostcode', e.target.value.toUpperCase())}
                    placeholder="LS6 3AB"
                    className={`${inpCls(!!locErr)} font-mono tracking-[0.04em]`}
                  />
                  <p className={HINT}>Required in address mode</p>
                </FormField>
              </>
            )}
          </div>
        )
      },
    },
    {
      key: 'what', title: 'What happened', sub: 'Circumstances and police', icon: <KIcon name="pulse" size={17} />,
      fields: ['accidentDescription', 'accidentDescriptionOther', 'policeInvolvement'],
      view: (ctx) => (
        <dl className={DL}>
          <Kv label="Accident description" full>{a.accidentDescription ? <span className={BIG}>{descLabel(a.accidentDescription)}</span> : <Empty onAdd={ctx.onAdd} />}</Kv>
          <Kv label="Please describe" cond="if Other">
            {a.accidentDescription === 'other'
              ? (a.accidentDescriptionOther.trim() ? <span className="min-w-0">{a.accidentDescriptionOther}</span> : <Empty onAdd={ctx.onAdd} />)
              : <Muted>Only asked when the description is “Other”</Muted>}
          </Kv>
          <Kv label="Police involvement"><YesNo value={a.policeInvolvement} onAdd={ctx.onAdd} /></Kv>
        </dl>
      ),
      edit: (ctx) => (
        <div className={FGRID}>
          <div className="sm:col-span-2">
            <FormField variant="info" label="Accident description" fieldKey="accident.accident-description" htmlFor={`${ctx.idPrefix}-desc`}>
              <select id={`${ctx.idPrefix}-desc`} value={a.accidentDescription || ''} onChange={(e) => update('accidentDescription', e.target.value || null)} className={`${inpCls(false)} ${SELECT_BG}`}>
                <option value="">Select</option>
                {DESCRIPTIONS.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
              </select>
            </FormField>
          </div>
          {a.accidentDescription === 'other' && (
            <div className={`sm:col-span-2 ${REVEALED}`}>
              <FormField variant="info" label={<CondLabel label="Please describe" cond="shown for Other" />} fieldKey="accident.please-describe" htmlFor={`${ctx.idPrefix}-other`}>
                <input id={`${ctx.idPrefix}-other`} type="text" value={a.accidentDescriptionOther} onChange={(e) => update('accidentDescriptionOther', e.target.value)} className={inpCls(false)} />
              </FormField>
            </div>
          )}
          <FormField variant="info" label="Police involvement" fieldKey="accident.police-involvement">
            <SegRadios
              name={`${ctx.idPrefix}-police`}
              ariaLabel="Police involvement"
              options={[true, false].map((val) => ({ label: val ? 'Yes' : 'No', checked: a.policeInvolvement === val, onSelect: () => update('policeInvolvement', val) }))}
            />
          </FormField>
        </div>
      ),
    },
    {
      key: 'vehicle', title: 'Claimant vehicle', sub: 'The vehicle the client was in', icon: <KIcon name="car" size={17} />,
      fields: ['vehiclePosition', 'claimantReg', 'driverName', 'wearingSeatbelt', 'numberOfPeopleInVehicle', 'claimantVehicleResponsible'],
      errorKeys: ['claimantReg'],
      view: (ctx) => (
        <dl className={DL}>
          <Kv label="Driver or passenger">
            {a.vehiclePosition ? <span className={CHIP}><KIcon name="user" size={12} />{a.vehiclePosition === 'driver' ? 'Driver' : 'Passenger'}</span> : <Empty onAdd={ctx.onAdd} />}
          </Kv>
          <Kv label="Claimant reg">{a.claimantReg.trim() ? <span className={PLATE}>{a.claimantReg}</span> : <Empty onAdd={ctx.onAdd} />}</Kv>
          {isPassenger && (
            <Kv label="Driver name" cond="if Passenger" full>{a.driverName.trim() ? <span className="min-w-0">{a.driverName}</span> : <Empty onAdd={ctx.onAdd} />}</Kv>
          )}
          <Kv label="Wearing a seatbelt"><YesNo value={a.wearingSeatbelt} onAdd={ctx.onAdd} /></Kv>
          <Kv label="People in vehicle">{a.numberOfPeopleInVehicle !== null ? <span className="tabular-nums">{a.numberOfPeopleInVehicle}</span> : <Empty onAdd={ctx.onAdd} />}</Kv>
          <Kv label="Passenger in the vehicle responsible" full><YesNo value={a.claimantVehicleResponsible} onAdd={ctx.onAdd} /></Kv>
        </dl>
      ),
      edit: (ctx) => {
        const regErr = ctx.err('claimantReg')
        return (
          <div className={FGRID}>
            <FormField variant="info" label="Driver or passenger" fieldKey="accident.driver-or-passenger">
              <SegRadios
                name={`${ctx.idPrefix}-position`}
                ariaLabel="Driver or passenger"
                options={(['driver', 'passenger'] as const).map((option) => ({
                  label: option === 'driver' ? 'Driver' : 'Passenger',
                  checked: a.vehiclePosition === option,
                  onSelect: () => update('vehiclePosition', option),
                }))}
              />
            </FormField>
            <FormField variant="info" label="Claimant reg" fieldKey="accident.claimant-reg" required={accidentFieldRequired('claimantReg', currentCase)} htmlFor={`${ctx.idPrefix}-reg`}>
              <input
                id={`${ctx.idPrefix}-reg`}
                type="text"
                value={a.claimantReg}
                onChange={(e) => update('claimantReg', e.target.value.toUpperCase())}
                className={`${inpCls(!!regErr)} font-mono tracking-[0.04em]`}
                placeholder="Enter registration"
                aria-invalid={regErr ? true : undefined}
              />
              <FieldError message={regErr} />
            </FormField>
            {isPassenger && (
              <div className={`sm:col-span-2 ${REVEALED}`}>
                <FormField variant="info" label={<CondLabel label="Driver name" cond="shown for Passenger" />} fieldKey="accident.driver-name" htmlFor={`${ctx.idPrefix}-driver`}>
                  <input id={`${ctx.idPrefix}-driver`} type="text" value={a.driverName} onChange={(e) => update('driverName', e.target.value)} className={inpCls(false)} />
                </FormField>
              </div>
            )}
            <FormField variant="info" label="Wearing a seatbelt" fieldKey="accident.wearing-a-seatbelt">
              <SegRadios
                name={`${ctx.idPrefix}-seatbelt`}
                ariaLabel="Wearing a seatbelt"
                options={[true, false].map((val) => ({ label: val ? 'Yes' : 'No', checked: a.wearingSeatbelt === val, onSelect: () => update('wearingSeatbelt', val) }))}
              />
            </FormField>
            <FormField variant="info" label="People in the claimant vehicle" fieldKey="accident.number-of-people-in-the-claimant-vehicle" htmlFor={`${ctx.idPrefix}-people`}>
              <input id={`${ctx.idPrefix}-people`} type="number" min={1} value={a.numberOfPeopleInVehicle || ''} onChange={(e) => {
                // Clamp — an out-of-range number was rejected server-side and then
                // blocked every later save of the case.
                const n = e.target.value ? parseInt(e.target.value, 10) : NaN
                update('numberOfPeopleInVehicle', Number.isFinite(n) ? Math.min(Math.max(n, 0), 99) : null)
              }} className={INP + ' border-casevo-border tabular-nums'} />
            </FormField>
            <div className="sm:col-span-2">
              <FormField variant="info" label="Was the claimant a passenger in the vehicle responsible" fieldKey="accident.was-the-claimant-a-passenger-in-the-vehicle-responsible">
                <SegRadios
                  name={`${ctx.idPrefix}-responsible`}
                  ariaLabel="Was the claimant a passenger in the vehicle responsible"
                  options={[true, false].map((val) => ({ label: val ? 'Yes' : 'No', checked: a.claimantVehicleResponsible === val, onSelect: () => update('claimantVehicleResponsible', val) }))}
                />
              </FormField>
            </div>
          </div>
        )
      },
    },
  ]

  // ── at-a-glance (mockup accGl) ──
  const where = freeText
    ? (htmlText(a.accidentLocationFreeText) || null)
    : (a.accidentLocationAddressLine1.trim() || a.accidentLocationAddressTown.trim() || pc || a.accidentLocation.trim() || null)
  const glance = (
    <Glance cells={[
      {
        icon: 'cal', label: 'When',
        value: a.accidentDate ? <span className="tabular-nums">{shortDate(a.accidentDate)}{a.accidentTime ? ` · ${a.accidentTime}` : ''}</span> : <GlanceDash />,
        sub: a.accidentDate ? `${civil(a.accidentDate)?.toLocaleDateString('en-GB', { weekday: 'long' }) ?? ''} · ${daysAgo(a.accidentDate)}` : 'Date of accident',
      },
      {
        icon: 'map', label: 'Where',
        value: where ? <>{where}{!freeText && pc && where !== pc ? <> · <span className="font-mono text-[13px]">{pc}</span></> : null}</> : <GlanceDash />,
        sub: freeText ? 'Free text' : (a.accidentLocationAddressTown.trim() || 'Accident location'),
      },
      {
        icon: 'pulse', label: 'Type',
        value: a.accidentDescription ? (a.accidentDescription === 'other' && a.accidentDescriptionOther.trim() ? a.accidentDescriptionOther : descLabel(a.accidentDescription)) : <GlanceDash />,
        sub: a.policeInvolvement === true ? 'Police involved' : a.policeInvolvement === false ? 'No police involvement' : 'Police not recorded',
      },
      {
        icon: 'car', label: 'Claimant reg',
        value: a.claimantReg.trim() ? <span className="font-mono">{a.claimantReg}</span> : <GlanceDash />,
        sub: [a.vehiclePosition === 'driver' ? 'Driver' : a.vehiclePosition === 'passenger' ? 'Passenger' : null,
          a.wearingSeatbelt === true ? 'seatbelt on' : a.wearingSeatbelt === false ? 'no seatbelt' : null].filter(Boolean).join(' · ') || 'Claimant vehicle',
      },
    ]} />
  )

  return (
    <SectionCards<AccidentDetails>
      value={accident}
      onChange={updateMany}
      fieldKeyOf={(f) => FIELD_KEY[f]}
      onBroadcast={broadcastFieldEdits}
      onFlush={flushAutoSave}
      errors={errors}
      defs={defs}
      title="Accident details"
      sub={showPrompt ? 'Add the accident details when you have them' : 'What happened, when and where.'}
      glance={glance}
      footer={<SectionFooter sectionKey="accidentDetails" errors={errors} />}
      idPrefix={`acc-${currentCase.id}`}
    />
  )
}
export default AccidentDetailsSection
